import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "modal-wrapper modal-content__modal-wrapper" }
const _hoisted_2 = { class: "content-container modal-wrapper__content-container" }
const _hoisted_3 = {
  key: 0,
  class: "title content-container__title"
}
const _hoisted_4 = { class: "title__title-text" }
const _hoisted_5 = { class: "content-container__content-wrapper" }

import { i18n } from '@/i18n';
import { onMounted, onUnmounted, ref, watch } from 'vue';

type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'auto' | 'max-content';

// props

export default /*@__PURE__*/_defineComponent({
  __name: 'Modal',
  props: {
    modalState: { type: Boolean },
    modalTitle: { default: '' },
    modalSize: { default: 'md' },
    modalWidth: { default: undefined },
    aspectRatio: { type: Boolean, default: false },
    scrollOff: { type: Boolean, default: false },
    padding: { default: '30px' },
    mdClickOutSideToClose: { type: Boolean, default: true },
    stretchContainer: { type: Boolean, default: false },
    noPaddingAndMargin: { type: Boolean, default: false },
    borderRadius: { default: '0' },
    fillBackdrop: { type: Boolean, default: true },
    noCancel: { type: Boolean, default: false },
    isModalOverflowHidden: { type: Boolean, default: false }
  },
  emits: ["outside-click"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "63f0b9b6": (props.modalSize === `auto` || props.modalSize === `max-content` ? getWidth(props.modalSize) : getWidth(props.modalSize) + `px`),
  "4f00a0ea": (props.aspectRatio ? getHeight(props.modalSize) : `fit-content`),
  "333d8533": (props.scrollOff ? props.isModalOverflowHidden ? `hidden` : `` : `scroll`),
  "78630fb9": (props.borderRadius),
  "52987001": (props.noPaddingAndMargin ? `0.4em 2em` : `0`),
  "b141543e": (props.stretchContainer ? `100%` : `auto`),
  "3aabeb56": (props.padding)
}))

const props = __props;

// emits
const emits = __emit;

/**
 * モーダル幅を取得する
 * @param size - モーダルサイズ
 * @returns 設定する幅(px or auto/max-content)
 */
const getWidth = (size: ModalSize | number) => {
  if (props.modalWidth) {
    if (props.modalSize === 'auto' || props.modalSize === 'max-content') {
      return props.modalWidth + 'px';
    } else {
      return props.modalWidth;
    }
  }

  if (!size) {
    return 720;
  } else if (typeof size === 'string') {
    switch (size) {
      case 'xs':
        return 300;
      case 'sm':
        return 540;
      case 'md':
        return 720;
      case 'lg':
        return 900;
      case 'xl':
        return 1080;
      case '2xl':
        return 1200;
      case '3xl':
        return 1320;
      case 'auto':
        return 'auto';
      case 'max-content':
        return 'max-content';
    }
  } else {
    return size;
  }
};

/**
 * モーダル高さを取得する
 * @param size - モーダルサイズ
 * @returns 設定する高さ(パーセンテージ or auto/max-content)
 */
const getHeight = (size: ModalSize | number): string => {
  if (!size) {
    return `${720 * 0.75}px`;
  } else if (typeof size === 'string') {
    switch (size) {
      case 'xs':
        return '40%';
      case 'sm':
        return '40%';
      case 'md':
        return '50%';
      case 'lg':
        return '60%';
      case 'xl':
        return '70%';
      case '2xl':
        return '80%';
      case '3xl':
        return '80%';
      case 'auto':
        return 'auto';
      case 'max-content':
        return 'max-content';
    }
  } else {
    return `${size * 0.75}px`;
  }
};

// 変数
const modalRef = ref<HTMLDivElement>(); // モーダルのref
const t = i18n.t;

/**
 * モーダルを表示するときの処理
 */
const openModal = () => {
  document.body.style.overflow = 'hidden'; // 背景スクロールを抑制
  // ESCキーの制御 (必要であれば)
  if (props.noCancel) {
    window.addEventListener('keydown', preventEsc);
  }
};

/**
 * モーダルを閉じるときの処理
 */
const closeModal = () => {
  document.body.style.overflow = ''; // 背景スクロールを戻す
  // ESCキーの制御を解除
  if (props.noCancel) {
    window.removeEventListener('keydown', preventEsc);
  }
};

/**
 * 外側クリックで閉じるときのハンドラ
 * @param e - クリックイベント
 */
const onClickModal = (e: MouseEvent) => {
  // クリックした要素がモーダルのラッパ要素そのものであれば閉じる
  if (e.target === modalRef.value && props.mdClickOutSideToClose) {
    emits('outside-click', false);
  }
};

/**
 * ESCキー押下を無効にする
 * @param e - キーボードイベント
 */
function preventEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault();
  }
}

// 親コンポーネントから渡ってくる modalState の変化を監視
watch(
  () => props.modalState,
  (newVal) => {
    if (newVal) {
      openModal();
    } else {
      closeModal();
    }
  }
);

// 初期表示時の対応
onMounted(() => {
  if (props.modalState) {
    openModal();
  }
});

onUnmounted(() => {
  if (props.modalState) {
    closeModal();
  }
});

return (_ctx: any,_cache: any) => {
  return (_ctx.modalState)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "modalRef",
        ref: modalRef,
        class: _normalizeClass(["modal-content", { 'modal-content--fill-backdrop': _ctx.fillBackdrop }]),
        onClick: onClickModal
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            (_ctx.modalTitle !== '')
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)(_ctx.modalTitle)), 1)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_5, [
              _renderSlot(_ctx.$slots, "default")
            ])
          ])
        ])
      ], 2))
    : _createCommentVNode("", true)
}
}

})